import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useNavigate, Link } from 'react-router'
import { Dropdown, Modal } from 'react-bootstrap'
import XLSX from 'xlsx'

import FilterPagination from 'pages/parts/FilterPagination'
import DeleteModal from 'pages/parts/DeleteModal'

import { httpOk, GET, POST, DELETE } from 'helpers/api'
import { returnSign, formatDate } from "helpers/helpers"

// Выпуск дисконтных карт партиями: номера EAN-13 генерирует сервер,
// файл уходит в типографию, а карты из пачки потом привязывают к клиентам
function CardsBatches() {
	const { t } = useTranslation()
	const navigate = useNavigate()

	const [data, setData] = useState({})
	const [selectedItem, setSelectedItem] = useState({})
	const [showModal, setShowModal] = useState(false)
	const [modalDelete, setModalDelete] = useState(false)
	const [deleteId, setDeleteId] = useState(0)
	const [filterData, setFilterData] = useState({})
	const [pagination, setPagination] = useState({
		url: '/clients-cards-batches',
		response: null
	})

	async function searchWithFilter() {
		var filterUrl = "/clients-cards-batches"
		var urlParams = ""
		if (filterData.page)
			urlParams += returnSign(urlParams) + 'page=' + filterData.page

		filterUrl = filterUrl + urlParams
		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}
	}

	async function create(e) {
		e.preventDefault()

		const response = await POST('/clients-cards-batches', selectedItem, { loader: true })
		if (httpOk(response)) {
			setShowModal(false)
			searchWithFilter()
			// Файл для типографии нужен сразу после выпуска — скачиваем, не заставляя искать партию в списке
			exportExcel(response.data.id)
		}
	}

	// Номера отдаём строками: иначе Excel покажет 13 цифр как 2,9E+12 и типография получит мусор
	async function exportExcel(id) {
		const response = await GET(`/clients-cards-batches/${id}/numbers`, {}, { loader: true })
		if (!httpOk(response)) return

		const rows = [['№', t('card_number')]]
		response.data.numbers.forEach((number, index) => rows.push([index + 1, String(number)]))

		const wb = XLSX.utils.book_new()
		const ws = XLSX.utils.aoa_to_sheet(rows)
		ws['!cols'] = [{ wch: 8 }, { wch: 18 }]
		XLSX.utils.book_append_sheet(wb, ws, t('cards_batch'))
		XLSX.writeFile(wb, `${t('cards_batch')} ${id}.xlsx`)
	}

	async function deleteItem() {
		const response = await DELETE(`/clients-cards-batches/${deleteId}`, {}, { loader: true })
		if (httpOk(response)) {
			setModalDelete(false)
			searchWithFilter()
		}
	}

	function toggleModal(bool = false) {
		setSelectedItem({
			'prefix': data.default_prefix ?? '29',
			'quantity': '',
			'numbering': 'random',
			'note': '',
		})
		setShowModal(bool)
	}

	function paginate(page) {
		setFilterData({ ...filterData, 'page': page })
	}

	function isValidForm() {
		const quantity = Number(selectedItem.quantity)
		return /^2[2-9][0-9]{0,2}$/.test(String(selectedItem.prefix ?? ''))
			&& quantity >= 1 && quantity <= (data.max_quantity ?? 10000)
	}

	useEffect(() => {
		searchWithFilter()
	}, [filterData.page]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<h4 className="vertical-center">
					{t('clients_cards_batches')} [{data.total ?? 0}]
				</h4>
				<div className="d-flex">
					<button className="btn-rounded btn btn-primary"
						onClick={() => toggleModal(true)}>
						{t('cards_issue_batch')}
					</button>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<p className="text-muted mb-3">{t('cards_batches_hint')}</p>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th>{t('cards_range')}</th>
									<th className="text-center">{t('prefix')}</th>
									<th>{t('cards_numbering')}</th>
									<th className="text-end">{t('quantity')}</th>
									<th className="text-end">{t('cards_free')}</th>
									<th className="text-end">{t('cards_issued')}</th>
									<th className="text-end">{t('cards_blocked')}</th>
									<th>{t('note')}</th>
									<th>{t('created_by')}</th>
									<th className="text-center">{t('date')}</th>
									<th style={{ 'minWidth': '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={index}>
										<td className="text-center">{item.row_number}</td>
										{/* Клик по номерам — карты этой партии. У партии вразброс диапазона нет:
											между первым и последним номером лежат чужие, поэтому показываем номер партии */}
										<td className="text-nowrap">
											<Link to={`/clients-cards?batch_id=${item.id}`}>
												{item.numbering === 'sequential' && item.first_number ?
													`${item.first_number} – ${item.last_number}` :
													`${t('cards_batch')} №${item.id}`
												}
											</Link>
										</td>
										<td className="text-center">{item.prefix}</td>
										<td>{item.numbering === 'sequential' ? t('cards_numbering_sequential') : t('cards_numbering_random')}</td>
										<td className="text-end">{item.quantity}</td>
										<td className="text-end">{item.count_free}</td>
										<td className="text-end">{item.count_issued}</td>
										<td className="text-end">{item.count_blocked}</td>
										<td className="td-ellipsis">{item.note}</td>
										<td>{item.created_by}</td>
										<td className="text-center text-nowrap">{formatDate(item.created_at)}</td>
										<td>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => exportExcel(item.id)}>
															<i className="uil-file-download-alt"></i>
															{t('cards_export_printing')}
														</div>
														<div className="table-dropdown-item"
															onClick={() => navigate(`/clients-cards?batch_id=${item.id}`)}>
															<i className="uil-credit-card"></i>
															{t('cards_show_cards')}
														</div>
														{Number(item.count_issued) === 0 && Number(item.count_blocked) === 0 &&
															<div className="table-dropdown-item text-danger"
																onClick={() => {
																	setDeleteId(item.id)
																	setModalDelete(true)
																}}>
																<i className="uil-times"></i>
																{t('delete')}
															</div>
														}
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* MODAL BATCH */}
			<Modal show={showModal} animation={false} centered backdrop="static"
				size="md"
				onHide={() => toggleModal()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('cards_issue_batch')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={(e) => create(e)} autoComplete="off">
						<div className="form-group w-100">
							<label>{t('quantity')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" autoFocus
								value={selectedItem.quantity ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'quantity': e.target.value.replace(/[^0-9]/g, '') })} />
							<small className="text-muted">{t('cards_quantity_hint', { max: data.max_quantity ?? 10000 })}</small>
						</div>

						<div className="form-group w-100">
							<label>{t('prefix')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" maxLength={4}
								value={selectedItem.prefix ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'prefix': e.target.value.replace(/[^0-9]/g, '') })} />
							<small className="text-muted">{t('cards_prefix_hint')}</small>
						</div>

						<div className="form-group w-100">
							<label>{t('cards_numbering')}</label>
							<div className="form-check">
								<input className="form-check-input" type="radio" id="numbering_random"
									checked={selectedItem.numbering === 'random'}
									onChange={() => setSelectedItem({ ...selectedItem, 'numbering': 'random' })} />
								<label className="form-check-label" htmlFor="numbering_random">
									{t('cards_numbering_random')}
								</label>
							</div>
							<div className="form-check">
								<input className="form-check-input" type="radio" id="numbering_sequential"
									checked={selectedItem.numbering === 'sequential'}
									onChange={() => setSelectedItem({ ...selectedItem, 'numbering': 'sequential' })} />
								<label className="form-check-label" htmlFor="numbering_sequential">
									{t('cards_numbering_sequential')}
								</label>
							</div>
							<small className="text-muted">{t('cards_numbering_hint')}</small>
						</div>

						<div className="form-group w-100">
							<label>{t('note')}</label>
							<input type="text" className="form-control" maxLength={255}
								value={selectedItem.note ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'note': e.target.value })} />
						</div>

						<div className="d-flex w-100 mt-3 gap-2">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModal()}>{t('cancel')}</button>
							<button type="submit" className="btn btn-primary w-100"
								disabled={!isValidForm()}>
								{t('cards_issue_batch')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* MODAL BATCH */}

			<DeleteModal
				modalDelete={modalDelete}
				setModalDelete={setModalDelete}
				deleteItem={deleteItem}>
			</DeleteModal>
		</>
	)
}

export default CardsBatches
